/**
 * React Query hooks for job data management
 *
 * Provides hooks for fetching and mutating job data with
 * automatic cache invalidation and optimistic updates.
 */

import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { jobService } from '../services/api';
import {
  Job,
  JobCreate,
  JobUpdate,
  JobListResponse,
  JobFilters,
  BulkUploadResponse,
} from '../types/job';

const JOBS_QUERY_KEY = 'jobs';
const BATCHES_QUERY_KEY = 'batches';
const SESSIONS_QUERY_KEY = 'sessions';

/**
 * Hook to fetch list of jobs with filters
 */
export function useJobs(filters?: JobFilters): UseQueryResult<JobListResponse, Error> {
  return useQuery({
    queryKey: [JOBS_QUERY_KEY, filters],
    queryFn: () => jobService.list(filters),
    // Keep data fresh for 1 minute, refetch on window focus for real-time updates
    staleTime: 60000,
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook to fetch a single job by ID
 */
export function useJob(id: string | undefined): UseQueryResult<Job, Error> {
  return useQuery({
    queryKey: [JOBS_QUERY_KEY, id],
    queryFn: () => jobService.get(id!),
    enabled: !!id, // Only run query if id is provided
    staleTime: 60000,
    refetchOnWindowFocus: true, // Refetch on focus for real-time status updates
  });
}

/**
 * Hook to create a new job (with implicit agent creation if agent_id not provided)
 */
export function useCreateJob(): UseMutationResult<Job, Error, JobCreate> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: JobCreate) => jobService.create(data),
    onSuccess: () => {
      // Invalidate and refetch jobs list
      queryClient.invalidateQueries({ queryKey: [JOBS_QUERY_KEY] });
      // Also invalidate batches to update statistics
      queryClient.invalidateQueries({ queryKey: [BATCHES_QUERY_KEY] });
    },
  });
}

/**
 * Hook to update an existing job
 */
export function useUpdateJob(): UseMutationResult<Job, Error, { id: string; data: JobUpdate }> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: JobUpdate }) =>
      jobService.update(id, data),
    onSuccess: (updatedJob) => {
      // Invalidate jobs list
      queryClient.invalidateQueries({ queryKey: [JOBS_QUERY_KEY] });

      // Update the cache for the specific job
      queryClient.setQueryData(
        [JOBS_QUERY_KEY, updatedJob.job_id],
        updatedJob
      );
    },
  });
}

/**
 * Hook to delete a job
 */
export function useDeleteJob(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => jobService.delete(id),
    onSuccess: () => {
      // Invalidate and refetch jobs list
      queryClient.invalidateQueries({ queryKey: [JOBS_QUERY_KEY] });
      // Also invalidate batches to update statistics
      queryClient.invalidateQueries({ queryKey: [BATCHES_QUERY_KEY] });
    },
  });
}

/**
 * Hook to execute a single job
 */
export function useExecuteJob(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => jobService.execute(id),
    // Optimistic update: immediately set status to "running"
    onMutate: async (id: string) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: [JOBS_QUERY_KEY, id] });

      // Snapshot the previous value
      const previousJob = queryClient.getQueryData([JOBS_QUERY_KEY, id]);

      // Optimistically update to "running" status
      queryClient.setQueryData([JOBS_QUERY_KEY, id], (old: any) => {
        if (!old) return old;
        return { ...old, status: 'running', started_at: new Date().toISOString() };
      });

      return { previousJob };
    },
    onError: (err, id, context) => {
      // Rollback on error
      if (context?.previousJob) {
        queryClient.setQueryData([JOBS_QUERY_KEY, id], context.previousJob);
      }
    },
    onSuccess: (data, jobId) => {
      // Invalidate jobs list, batches, and sessions to update statistics
      queryClient.invalidateQueries({ queryKey: [JOBS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [BATCHES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [SESSIONS_QUERY_KEY, 'job', jobId] });
    },
  });
}

/**
 * Hook to execute all pending jobs in a batch
 */
export function useBatchExecuteJobs(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (batchId: string) => jobService.batchExecute(batchId),
    onSuccess: () => {
      // Invalidate jobs list and batches to update statistics
      queryClient.invalidateQueries({ queryKey: [JOBS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [BATCHES_QUERY_KEY] });
    },
  });
}

/**
 * Hook to retry/re-run a job (creates new session, not new job)
 *
 * Accepts optional settings to override project defaults.
 */
interface JobRunSettings {
  browser_type?: string;
  use_proxy?: boolean;
  model?: string;
  tinyctx_mode?: string;
  recording_id?: string;
}

interface JobRunParams {
  id: string;
  settings?: JobRunSettings;
}

export function useRetryJob(): UseMutationResult<void, Error, JobRunParams> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, settings }: JobRunParams) => jobService.execute(id, settings),
    onMutate: async ({ id }: JobRunParams) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: [JOBS_QUERY_KEY, id] });

      // Snapshot the previous value
      const previousJob = queryClient.getQueryData([JOBS_QUERY_KEY, id]);

      // Optimistically update to "running" status
      queryClient.setQueryData([JOBS_QUERY_KEY, id], (old: any) => {
        if (!old) return old;
        return { ...old, status: 'running' };
      });

      return { previousJob };
    },
    onError: (err, { id }, context) => {
      // Rollback on error
      if (context?.previousJob) {
        queryClient.setQueryData([JOBS_QUERY_KEY, id], context.previousJob);
      }
    },
    onSuccess: (data, { id }) => {
      // Invalidate to update job statistics and session list
      queryClient.invalidateQueries({ queryKey: [JOBS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [BATCHES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [SESSIONS_QUERY_KEY, 'job', id] });
    },
  });
}

/**
 * Hook to bulk upload jobs from CSV/JSON with natural language mapping
 */
export function useBulkUploadJobs(): UseMutationResult<
  BulkUploadResponse,
  Error,
  { batchId: string; file: File; mappingInstructions: string }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ batchId, file, mappingInstructions }: { batchId: string; file: File; mappingInstructions: string }) =>
      jobService.bulkUpload(batchId, file, mappingInstructions),
    onSuccess: () => {
      // Invalidate jobs list and batches to include new bulk uploaded jobs
      queryClient.invalidateQueries({ queryKey: [JOBS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [BATCHES_QUERY_KEY] });
    },
  });
}
